import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { Node as TipTapNode, Extension } from '@tiptap/core';
import Layout from '../components/Layout';
import { documentService, Document } from '../services/documentService';
import { geminiService } from '../services/geminiService';
import mermaid from 'mermaid';
import '../styles/DocumentEditor.css';
import {
    FaBold, FaItalic, FaUnderline, FaStrikethrough,
    FaListUl, FaListOl, FaQuoteRight, FaCode,
    FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify,
    FaLink, FaImage, FaUndo, FaRedo, FaChartBar, FaFileExport, FaCaretDown
} from 'react-icons/fa';

// Custom Mermaid extension for TipTap
const MermaidDiagram = TipTapNode.create({
    name: 'mermaidDiagram',
    group: 'block',
    atom: true,

    addAttributes() {
        return {
            code: {
                default: '',
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div.mermaid-diagram',
                getAttrs: (node) => {
                    const pre = (node as HTMLElement).querySelector('pre.mermaid');
                    return {
                        code: pre?.textContent || '',
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            { class: 'mermaid-diagram' },
            ['pre', { class: 'mermaid', 'data-mermaid-code': HTMLAttributes.code }, HTMLAttributes.code],
        ];
    },
});

// Mermaid diagram templates
const MERMAID_TEMPLATES = {
    flowchart: `graph TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Action 1]
    B -->|No| D[Action 2]
    C --> E[End]
    D --> E`,
    sequence: `sequenceDiagram
    participant User
    participant System
    participant Database
    User->>System: Request
    System->>Database: Query
    Database-->>System: Data
    System-->>User: Response`,
    gantt: `gantt
    title Project Timeline
    dateFormat YYYY-MM-DD
    section Phase 1
    Planning       :a1, 2024-01-01, 7d
    Development    :a2, after a1, 14d
    section Phase 2
    Testing        :a3, after a2, 7d
    Deployment     :a4, after a3, 3d`,
    userJourney: `journey
    title User Purchase Journey
    section Discovery
      Browse products: 5: User
      Search for item: 3: User
    section Purchase
      Add to cart: 4: User
      Enter details: 2: User
      Complete payment: 5: User`,
    erDiagram: `erDiagram
    USER ||--o{ ORDER : places
    ORDER ||--|{ ORDER_ITEM : contains
    PRODUCT ||--o{ ORDER_ITEM : "ordered in"
    USER {
        string id
        string name
        string email
    }
    ORDER {
        string id
        date created_at
        string status
    }`,
    stateDiagram: `stateDiagram-v2
    [*] --> Draft
    Draft --> Review: Submit
    Review --> Approved: Approve
    Review --> Rejected: Reject
    Rejected --> Draft: Revise
    Approved --> Published: Publish
    Published --> [*]`,
    classDiagram: `classDiagram
    class User {
        +String name
        +String email
        +login()
        +logout()
    }
    class Product {
        +String title
        +Float price
        +getDetails()
    }
    User --> Product : purchases`,
    pie: `pie title Market Share
    "Product A" : 45
    "Product B" : 30
    "Product C" : 15
    "Others" : 10`
};

// Custom FontSize extension
const FontSize = Extension.create({
    name: 'fontSize',

    addOptions() {
        return {
            types: ['textStyle'],
        };
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
                        renderHTML: attributes => {
                            if (!attributes.fontSize) {
                                return {};
                            }
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setFontSize: (fontSize: string) => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontSize })
                    .run();
            },
            unsetFontSize: () => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontSize: null })
                    .removeEmptyTextStyle()
                    .run();
            },
        };
    },
});

const MenuBar = ({ editor, onInsertDiagram }: { editor: any; onInsertDiagram: () => void }) => {
    const [showFontFamily, setShowFontFamily] = useState(false);
    const [showFontSize, setShowFontSize] = useState(false);
    const fontFamilyRef = useRef<HTMLDivElement>(null);
    const fontSizeRef = useRef<HTMLDivElement>(null);

    // Close dropdowns on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (fontFamilyRef.current && !fontFamilyRef.current.contains(event.target as Node)) {
                setShowFontFamily(false);
            }
            if (fontSizeRef.current && !fontSizeRef.current.contains(event.target as Node)) {
                setShowFontSize(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (!editor) {
        return null;
    }

    const addImage = () => {
        const url = window.prompt('URL');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    };

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        // cancelled
        if (url === null) {
            return;
        }

        // empty
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        // update
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const fontFamilies = [
        { label: 'Default', value: '' },
        { label: 'Arial', value: 'Arial, sans-serif' },
        { label: 'Times New Roman', value: 'Times New Roman, serif' },
        { label: 'Courier New', value: 'Courier New, monospace' },
        { label: 'Georgia', value: 'Georgia, serif' },
        { label: 'Verdana', value: 'Verdana, sans-serif' },
        { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
        { label: 'Poppins', value: 'Poppins, sans-serif' },
    ];

    const fontSizes = [
        { label: 'Small', value: '12px' },
        { label: 'Normal', value: '16px' },
        { label: 'Large', value: '20px' },
        { label: 'Huge', value: '24px' },
        { label: '28px', value: '28px' },
        { label: '32px', value: '32px' },
    ];

    const currentFontFamily = editor.getAttributes('textStyle').fontFamily || '';
    const currentFontSize = editor.getAttributes('textStyle').fontSize || '';

    const getFontLabel = (value: string) => {
        const font = fontFamilies.find(f => f.value === value);
        return font ? font.label : 'Font';
    };

    const getSizeLabel = (value: string) => {
        const size = fontSizes.find(s => s.value === value);
        return size ? size.label : 'Size';
    };

    return (
        <div className="editor-toolbar">
            <div className="toolbar-group">
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
                    disabled={!editor.can().chain().focus().toggleBold().run()}
                    className={editor.isActive('bold') ? 'is-active' : ''}
                    title="Bold"
                >
                    <FaBold />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
                    disabled={!editor.can().chain().focus().toggleItalic().run()}
                    className={editor.isActive('italic') ? 'is-active' : ''}
                    title="Italic"
                >
                    <FaItalic />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
                    className={editor.isActive('underline') ? 'is-active' : ''}
                    title="Underline"
                >
                    <FaUnderline />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleStrike().run(); }}
                    disabled={!editor.can().chain().focus().toggleStrike().run()}
                    className={editor.isActive('strike') ? 'is-active' : ''}
                    title="Strike"
                >
                    <FaStrikethrough />
                </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 1 }).run(); }}
                    className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                    title="Heading 1"
                >
                    H1
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run(); }}
                    className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                    title="Heading 2"
                >
                    H2
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 3 }).run(); }}
                    className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                    title="Heading 3"
                >
                    H3
                </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
                <div className="font-dropdown-wrapper" ref={fontFamilyRef}>
                    <button
                        onClick={() => setShowFontFamily(!showFontFamily)}
                        className="font-dropdown-btn"
                        title="Font Family"
                    >
                        {getFontLabel(currentFontFamily)} <FaCaretDown style={{ marginLeft: '4px', fontSize: '10px' }} />
                    </button>
                    {showFontFamily && (
                        <div className="font-dropdown-menu">
                            {fontFamilies.map(font => (
                                <button
                                    key={font.value}
                                    onClick={() => {
                                        try {
                                            if (font.value) {
                                                editor.chain().focus().setFontFamily(font.value).run();
                                            } else {
                                                editor.chain().focus().unsetFontFamily().run();
                                            }
                                        } catch (error) {
                                            console.error('Error setting font family:', error);
                                        }
                                        setShowFontFamily(false);
                                    }}
                                    className={`font-dropdown-item ${currentFontFamily === font.value ? 'is-active' : ''}`}
                                    style={{ fontFamily: font.value }}
                                >
                                    {font.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="font-dropdown-wrapper" ref={fontSizeRef}>
                    <button
                        onClick={() => setShowFontSize(!showFontSize)}
                        className="font-dropdown-btn"
                        title="Font Size"
                    >
                        {getSizeLabel(currentFontSize)} <FaCaretDown style={{ marginLeft: '4px', fontSize: '10px' }} />
                    </button>
                    {showFontSize && (
                        <div className="font-dropdown-menu">
                            {fontSizes.map(size => (
                                <button
                                    key={size.value}
                                    onClick={() => {
                                        try {
                                            editor.chain().focus().setFontSize(size.value).run();
                                        } catch (error) {
                                            console.error('Error setting font size:', error);
                                        }
                                        setShowFontSize(false);
                                    }}
                                    className={`font-dropdown-item ${currentFontSize === size.value ? 'is-active' : ''}`}
                                    style={{ fontSize: size.value }}
                                >
                                    {size.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('left').run(); }}
                    className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
                    title="Align Left"
                >
                    <FaAlignLeft />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('center').run(); }}
                    className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
                    title="Align Center"
                >
                    <FaAlignCenter />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('right').run(); }}
                    className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
                    title="Align Right"
                >
                    <FaAlignRight />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('justify').run(); }}
                    className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
                    title="Justify"
                >
                    <FaAlignJustify />
                </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
                    className={editor.isActive('bulletList') ? 'is-active' : ''}
                    title="Bullet List"
                >
                    <FaListUl />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
                    className={editor.isActive('orderedList') ? 'is-active' : ''}
                    title="Ordered List"
                >
                    <FaListOl />
                </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBlockquote().run(); }}
                    className={editor.isActive('blockquote') ? 'is-active' : ''}
                    title="Quote"
                >
                    <FaQuoteRight />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleCodeBlock().run(); }}
                    className={editor.isActive('codeBlock') ? 'is-active' : ''}
                    title="Code Block"
                >
                    <FaCode />
                </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
                <button onClick={setLink} className={editor.isActive('link') ? 'is-active' : ''} title="Link">
                    <FaLink />
                </button>
                <button onClick={addImage} title="Image">
                    <FaImage />
                </button>
                <button onClick={onInsertDiagram} title="Insert Diagram" className="diagram-btn">
                    <FaChartBar />
                </button>
            </div>

            <div className="toolbar-divider"></div>

            <div className="toolbar-group">
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().undo().run(); }}
                    disabled={!editor.can().chain().focus().undo().run()}
                    title="Undo"
                >
                    <FaUndo />
                </button>
                <button
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().redo().run(); }}
                    disabled={!editor.can().chain().focus().redo().run()}
                    title="Redo"
                >
                    <FaRedo />
                </button>
            </div>
        </div>
    );
};

const DocumentEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { aiRequest?: { generatedContent: string }, isNewDoc?: boolean } | null;
    const aiRequest = locationState?.aiRequest;
    const typingRef = useRef(false);
    const editorContentRef = useRef<HTMLDivElement>(null);

    const [currentDoc, setCurrentDoc] = useState<Document | null>(null);
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [showDiagramModal, setShowDiagramModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<keyof typeof MERMAID_TEMPLATES>('flowchart');
    const [diagramCode, setDiagramCode] = useState(MERMAID_TEMPLATES.flowchart);
    const [showExportMenu, setShowExportMenu] = useState(false);

    // Debug log
    console.log('DocumentEditor mounted:', {
        id,
        hasLocationState: !!locationState,
        hasAiRequest: !!aiRequest,
        hasGeneratedContent: !!aiRequest?.generatedContent,
        generatedContentLength: aiRequest?.generatedContent?.length
    });

    // Initialize Mermaid with automatic rendering
    useEffect(() => {
        mermaid.initialize({
            startOnLoad: true,
            theme: 'default',
            securityLevel: 'loose',
            fontFamily: 'Poppins, sans-serif',
        });

        // Run mermaid on existing elements
        const runMermaid = async () => {
            try {
                await mermaid.run({
                    querySelector: '.mermaid',
                });
                console.log('Mermaid.run() completed');
            } catch (error) {
                console.error('Error running mermaid.run():', error);
            }
        };

        runMermaid();
    }, []);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Disable link from StarterKit to avoid duplicate
                // (we'll configure it separately below)
            }),
            Underline,
            Link.configure({
                openOnClick: false,
            }),
            Image,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Placeholder.configure({
                placeholder: 'Start typing your document...',
            }),
            MermaidDiagram,
            TextStyle,
            FontFamily.configure({
                types: ['textStyle'],
            }),
            FontSize,
        ],
        content: '',
        onUpdate: ({ editor }) => {
            // Auto-save logic could go here
        },
    });

    useEffect(() => {
        if (id) {
            loadDocument(id);
        } else {
            setLoading(false);
        }
    }, [id]);

    // Update editor content when document loads (but not when AI is generating)
    useEffect(() => {
        if (editor && currentDoc?.content && !aiRequest && !typingRef.current) {
            // Only set content if it's different to avoid cursor jumping
            if (editor.getHTML() !== currentDoc.content) {
                console.log('Setting editor content from currentDoc');
                console.log('Content length:', currentDoc.content.length);
                console.log('Content preview:', currentDoc.content.substring(0, 500));
                editor.commands.setContent(currentDoc.content);

                // Trigger render after content is set
                setTimeout(() => {
                    console.log('Triggering render from content update effect');
                    renderMermaidDiagrams();
                }, 300);
            }
        }
    }, [editor, currentDoc, aiRequest]);

    // Render Mermaid diagrams
    const renderMermaidDiagrams = async () => {
        try {
            console.log('=== renderMermaidDiagrams called ===');

            // Wait a bit for DOM to be ready
            await new Promise(resolve => setTimeout(resolve, 100));

            // Find all pre.mermaid elements that haven't been rendered yet
            const mermaidElements = document.querySelectorAll('.ProseMirror pre.mermaid:not([data-processed])');
            console.log('Found unprocessed mermaid elements:', mermaidElements.length);

            if (mermaidElements.length === 0) {
                console.log('No unprocessed mermaid diagrams found');
                return;
            }

            // Process each diagram
            for (let i = 0; i < mermaidElements.length; i++) {
                const element = mermaidElements[i] as HTMLElement;

                // Mark as processed to avoid re-rendering
                element.setAttribute('data-processed', 'true');

                // Get the mermaid code - use innerHTML first, then decode HTML entities
                let code = element.innerHTML || element.textContent || '';

                // Decode HTML entities
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = code;
                code = tempDiv.textContent || tempDiv.innerText || '';

                // Clean up the code
                code = code.trim();

                console.log(`Processing diagram ${i}:`, code.substring(0, 150));

                if (!code.trim()) {
                    console.log(`Skipping empty diagram ${i}`);
                    continue;
                }

                try {
                    // Generate unique ID
                    const id = `mermaid-${Date.now()}-${i}`;

                    // Store the original code in a data attribute for later export
                    element.setAttribute('data-mermaid-code', code);

                    // Render the diagram — Mermaid output is SVG; assign via innerHTML
                    // (Mermaid sanitises its own output; external user content only reaches
                    // this path after passing through the Mermaid parser, not raw HTML)
                    const { svg } = await mermaid.render(id, code);
                    element.innerHTML = svg;
                    element.style.background = 'transparent';
                    element.style.padding = '0';

                    console.log(`Successfully rendered diagram ${i}`);
                } catch (error) {
                    console.error(`Error rendering diagram ${i}:`, error);

                    // Build error UI with DOM methods — never interpolate error.message or
                    // user-supplied `code` into an HTML string to avoid XSS.
                    const wrapper = document.createElement('div');
                    Object.assign(wrapper.style, {
                        color: 'red', padding: '10px', background: '#fee',
                        border: '1px solid #fcc', borderRadius: '4px',
                    });

                    const heading = document.createElement('strong');
                    heading.textContent = 'Error rendering diagram:';
                    wrapper.appendChild(heading);
                    wrapper.appendChild(document.createElement('br'));

                    const msg = document.createElement('span');
                    msg.textContent = error instanceof Error ? error.message : 'Unknown error';
                    wrapper.appendChild(msg);
                    wrapper.appendChild(document.createElement('br'));

                    const details = document.createElement('details');
                    details.style.marginTop = '8px';
                    const summary = document.createElement('summary');
                    summary.style.cursor = 'pointer';
                    summary.textContent = 'Show code';
                    details.appendChild(summary);
                    const pre = document.createElement('pre');
                    Object.assign(pre.style, {
                        marginTop: '8px', padding: '8px', background: 'white',
                        border: '1px solid #ddd', overflowX: 'auto',
                    });
                    pre.textContent = code; // textContent — never innerHTML
                    details.appendChild(pre);
                    wrapper.appendChild(details);

                    element.replaceChildren(wrapper);
                }
            }
        } catch (error) {
            console.error('Error in renderMermaidDiagrams:', error);
        }
    };

    // AI Streaming Effect (Simulated Typewriter)
    useEffect(() => {
        console.log('AI Effect triggered:', {
            hasAiRequest: !!aiRequest,
            hasGeneratedContent: !!aiRequest?.generatedContent,
            hasEditor: !!editor,
            isTyping: typingRef.current
        });

        if (aiRequest?.generatedContent && editor && !typingRef.current) {
            console.log('Starting AI typing animation...');
            console.log('Content to display:', aiRequest.generatedContent.substring(0, 500));

            const simulateTyping = async () => {
                try {
                    typingRef.current = true;
                    setSaving(true);

                    const content = aiRequest.generatedContent;
                    console.log('AI generated content length:', content.length);

                    // Instead of character-by-character, just set the content directly
                    // This is more reliable and won't break HTML tags
                    editor.commands.setContent(content);
                    editor.setEditable(true);
                    setSaving(false);
                    typingRef.current = false;

                    console.log('Content set in editor');
                    console.log('Editor HTML:', editor.getHTML().substring(0, 500));

                    // Render mermaid diagrams after a short delay
                    setTimeout(() => {
                        renderMermaidDiagrams();
                    }, 100);

                    // Save content to database
                    if (id) {
                        await documentService.updateDocument(id, {
                            content: content,
                            updated_at: new Date().toISOString()
                        });
                        setLastSaved(new Date());
                        console.log('Content saved to database');
                    }

                    // Clear state to prevent re-run
                    window.history.replaceState({}, '');
                } catch (error) {
                    console.error('Error displaying content:', error);
                    editor?.setEditable(true);
                    setSaving(false);
                    typingRef.current = false;
                }
            };

            simulateTyping();
        }
    }, [aiRequest, editor, id]);

    // Render mermaid diagrams when editor content changes
    useEffect(() => {
        if (editor) {
            // Render diagrams whenever the editor content updates
            const timer = setTimeout(() => {
                renderMermaidDiagrams();
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [editor?.state.doc]);

    // Use MutationObserver to detect when mermaid elements are added to DOM
    useEffect(() => {
        const editorElement = document.querySelector('.ProseMirror');
        if (!editorElement) return;

        const observer = new MutationObserver((mutations) => {
            let shouldRender = false;
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement) {
                        if (node.classList?.contains('mermaid-diagram') ||
                            node.classList?.contains('mermaid') ||
                            node.querySelector?.('pre.mermaid') ||
                            node.querySelector?.('.mermaid-diagram')) {
                            console.log('MutationObserver detected new mermaid element');
                            shouldRender = true;
                        }
                    }
                });
            });

            if (shouldRender) {
                console.log('MutationObserver triggering diagram render');
                setTimeout(() => renderMermaidDiagrams(), 200);
            }
        });

        observer.observe(editorElement, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    }, [editor]);

    const loadDocument = async (docId: string) => {
        try {
            const doc = await documentService.getDocument(docId);
            if (doc) {
                setCurrentDoc(doc);
                setTitle(doc.title);
                setLastSaved(new Date(doc.updated_at));

                // Render mermaid diagrams after document loads
                setTimeout(() => {
                    console.log('Triggering initial diagram render after document load');
                    renderMermaidDiagrams();
                }, 500);
            } else {
                alert('Document not found');
                navigate('/docs-writer');
            }
        } catch (error) {
            console.error('Error loading document:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!id || !editor) return;

        setSaving(true);
        try {
            const content = editor.getHTML();
            await documentService.updateDocument(id, {
                title,
                content,
                updated_at: new Date().toISOString()
            });
            setLastSaved(new Date());
        } catch (error) {
            console.error('Error saving document:', error);
            alert('Failed to save document');
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate('/docs-writer');
    };

    const handleInsertDiagram = () => {
        setShowDiagramModal(true);
    };

    const handleTemplateChange = (template: keyof typeof MERMAID_TEMPLATES) => {
        setSelectedTemplate(template);
        setDiagramCode(MERMAID_TEMPLATES[template]);
    };

    const handleInsertDiagramConfirm = () => {
        if (!editor || !diagramCode.trim()) return;

        // Insert using the mermaidDiagram node type
        editor.chain().focus().insertContent({
            type: 'mermaidDiagram',
            attrs: {
                code: diagramCode,
            },
        }).insertContent('<p></p>').run();

        setShowDiagramModal(false);

        // Render the newly inserted diagram with a longer delay
        setTimeout(() => {
            console.log('Triggering diagram render after insert');
            renderMermaidDiagrams();
        }, 300);
    };

    const handleCancelDiagram = () => {
        setShowDiagramModal(false);
        setSelectedTemplate('flowchart');
        setDiagramCode(MERMAID_TEMPLATES.flowchart);
    };

    const handleExportPDF = async () => {
        if (!editor) return;

        try {
            // Get the editor content
            let content = editor.getHTML();

            // Find all mermaid diagrams and render them as SVG
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;
            const mermaidElements = tempDiv.querySelectorAll('pre.mermaid');

            // Render each mermaid diagram
            for (let i = 0; i < mermaidElements.length; i++) {
                const element = mermaidElements[i] as HTMLElement;
                let code = element.textContent || '';

                // Decode HTML entities
                const decoder = document.createElement('div');
                decoder.innerHTML = code;
                code = decoder.textContent || decoder.innerText || '';
                code = code.trim();

                if (code) {
                    try {
                        const id = `mermaid-export-${Date.now()}-${i}`;
                        const { svg } = await mermaid.render(id, code);
                        element.innerHTML = svg;
                        element.style.background = 'transparent';
                        element.style.padding = '0';
                    } catch (error) {
                        console.error('Error rendering mermaid for export:', error);
                        // Leave the code as-is if rendering fails
                    }
                }
            }

            content = tempDiv.innerHTML;

            const printWindow = window.open('', '', 'width=800,height=600');
            if (printWindow) {
                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>${title || 'Document'}</title>
                        <style>
                            body { font-family: Arial, sans-serif; padding: 40px; }
                            h1 { font-size: 24px; margin-bottom: 20px; }
                            h2 { font-size: 18px; margin-top: 20px; }
                            p { margin-bottom: 10px; }
                            .mermaid-diagram { margin: 20px 0; text-align: center; }
                            .mermaid-diagram svg { max-width: 100%; height: auto; }
                        </style>
                    </head>
                    <body>
                        <h1>${title || 'Untitled Document'}</h1>
                        ${content}
                    </body>
                    </html>
                `);
                printWindow.document.close();
                printWindow.print();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setShowExportMenu(false);
        }
    };

    const handleExportDOCX = async () => {
        if (!editor) return;

        try {
            console.log('=== Starting DOCX Export ===');

            // Find all rendered Mermaid SVGs in the live DOM
            const editorElement = document.querySelector('.ProseMirror');
            if (!editorElement) {
                console.error('Editor element not found');
                return;
            }

            const liveMermaidPres = editorElement.querySelectorAll('pre.mermaid[data-processed="true"]');
            console.log(`Found ${liveMermaidPres.length} rendered Mermaid diagrams in DOM`);

            // Store SVG to PNG conversions
            const svgToPngMap = new Map<Element, string>();

            // Convert each SVG to PNG
            for (let i = 0; i < liveMermaidPres.length; i++) {
                const preElement = liveMermaidPres[i];
                const svgElement = preElement.querySelector('svg');

                if (svgElement) {
                    console.log(`Converting diagram ${i + 1}/${liveMermaidPres.length} to PNG`);

                    try {
                        // Get SVG as string
                        const svgString = svgElement.outerHTML;

                        // Get SVG dimensions
                        const svgWidth = svgElement.width.baseVal.value || 800;
                        const svgHeight = svgElement.height.baseVal.value || 600;

                        // Convert SVG to PNG using canvas
                        const canvas = document.createElement('canvas');
                        canvas.width = svgWidth;
                        canvas.height = svgHeight;
                        const ctx = canvas.getContext('2d');

                        if (ctx) {
                            // Create image from SVG
                            const img = document.createElement('img') as HTMLImageElement;
                            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                            const svgUrl = URL.createObjectURL(svgBlob);

                            await new Promise<void>((resolve, reject) => {
                                img.onload = () => {
                                    ctx.fillStyle = 'white';
                                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                                    ctx.drawImage(img, 0, 0);
                                    URL.revokeObjectURL(svgUrl);
                                    resolve();
                                };
                                img.onerror = () => {
                                    URL.revokeObjectURL(svgUrl);
                                    reject(new Error('Failed to load SVG'));
                                };
                                img.src = svgUrl;
                            });

                            // Convert canvas to PNG data URL
                            const pngDataUrl = canvas.toDataURL('image/png');
                            svgToPngMap.set(preElement, pngDataUrl);
                            console.log(`Successfully converted diagram ${i + 1}`);
                        }
                    } catch (error) {
                        console.error(`Error converting diagram ${i + 1}:`, error);
                    }
                }
            }

            // Now get HTML and replace SVGs with PNGs
            let content = editor.getHTML();
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;

            // Find all mermaid-diagram divs in the exported HTML
            const exportMermaidDivs = tempDiv.querySelectorAll('div.mermaid-diagram');
            console.log(`Found ${exportMermaidDivs.length} mermaid divs in export HTML`);

            // Replace each with the PNG we created
            exportMermaidDivs.forEach((div, index) => {
                if (index < liveMermaidPres.length) {
                    const pngDataUrl = svgToPngMap.get(liveMermaidPres[index]);
                    if (pngDataUrl) {
                        div.outerHTML = `<div style="text-align: center; margin: 20px 0;"><img src="${pngDataUrl}" alt="Diagram" style="max-width: 100%; height: auto;" /></div>`;
                        console.log(`Replaced diagram ${index + 1} in export`);
                    }
                }
            });

            content = tempDiv.innerHTML;

            const htmlContent = `
                <!DOCTYPE html>
                <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
                <head>
                    <meta charset='utf-8'>
                    <title>${title || 'Document'}</title>
                    <style>
                        body { font-family: 'Poppins', Arial, sans-serif; line-height: 1.6; padding: 40px; }
                        h1 { font-size: 24px; margin-bottom: 20px; }
                        h2 { font-size: 18px; margin-top: 20px; }
                        p { margin-bottom: 10px; }
                        img { max-width: 100%; height: auto; }
                    </style>
                </head>
                <body>
                    <h1>${title || 'Untitled Document'}</h1>
                    ${content}
                </body>
                </html>
            `;

            const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${title || 'document'}.doc`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            console.log('DOCX export completed successfully');
        } catch (error) {
            console.error('Error exporting DOCX:', error);
            alert('Failed to export document. Please try again.');
        } finally {
            setShowExportMenu(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="editor-loading">Loading document...</div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="document-editor-container">
                <div className="editor-header">
                    <div className="header-left">
                        <button className="back-button" onClick={handleBack}>
                            Back
                        </button>
                        <input
                            type="text"
                            className="document-title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Untitled Document"
                        />
                    </div>
                    <div className="header-right">
                        <span className="save-status">
                            {saving ? 'Saving...' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : 'Unsaved'}
                        </span>
                        <button className="save-button" onClick={handleSave} disabled={saving}>
                            Save
                        </button>
                        <div className="export-dropdown" style={{ position: 'relative', display: 'inline-block', marginLeft: '8px' }}>
                            <button
                                className="save-button"
                                onClick={() => setShowExportMenu(!showExportMenu)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <FaFileExport />
                                Export
                                <FaCaretDown style={{ fontSize: '12px' }} />
                            </button>
                            {showExportMenu && (
                                <div
                                    className="export-menu"
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '4px',
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                        minWidth: '150px',
                                        zIndex: 1000
                                    }}
                                >
                                    <button
                                        onClick={handleExportPDF}
                                        style={{
                                            width: '100%',
                                            padding: '10px 16px',
                                            border: 'none',
                                            background: 'transparent',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        Export as PDF
                                    </button>
                                    <button
                                        onClick={handleExportDOCX}
                                        style={{
                                            width: '100%',
                                            padding: '10px 16px',
                                            border: 'none',
                                            background: 'transparent',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        Export as DOCX
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="editor-wrapper">
                    <MenuBar editor={editor} onInsertDiagram={handleInsertDiagram} />
                    <div className="editor-content-area">
                        <EditorContent editor={editor} className="tiptap-editor" />
                    </div>
                </div>

                {/* Diagram Insertion Modal */}
                {showDiagramModal && (
                    <div className="modal-overlay" onClick={handleCancelDiagram}>
                        <div className="modal-popup diagram-modal" onClick={(e) => e.stopPropagation()}>
                            <h2>Insert Mermaid Diagram</h2>
                            <p>Choose a template or customize the diagram code</p>

                            <div className="diagram-template-selector">
                                <label htmlFor="template-select">Diagram Type:</label>
                                <select
                                    id="template-select"
                                    value={selectedTemplate}
                                    onChange={(e) => handleTemplateChange(e.target.value as keyof typeof MERMAID_TEMPLATES)}
                                    className="template-dropdown"
                                >
                                    <option value="flowchart">Flowchart</option>
                                    <option value="sequence">Sequence Diagram</option>
                                    <option value="gantt">Gantt Chart</option>
                                    <option value="userJourney">User Journey</option>
                                    <option value="erDiagram">ER Diagram</option>
                                    <option value="stateDiagram">State Diagram</option>
                                    <option value="classDiagram">Class Diagram</option>
                                    <option value="pie">Pie Chart</option>
                                </select>
                            </div>

                            <div className="diagram-code-editor">
                                <label htmlFor="diagram-code">Mermaid Code:</label>
                                <textarea
                                    id="diagram-code"
                                    value={diagramCode}
                                    onChange={(e) => setDiagramCode(e.target.value)}
                                    rows={12}
                                    className="code-textarea"
                                    spellCheck={false}
                                />
                            </div>

                            <div className="diagram-help">
                                <p>
                                    <strong>Tip:</strong> Customize the code above or visit{' '}
                                    <a href="https://mermaid.js.org/syntax/flowchart.html" target="_blank" rel="noopener noreferrer">
                                        Mermaid Documentation
                                    </a>{' '}
                                    for more examples.
                                </p>
                            </div>

                            <div className="modal-actions">
                                <button className="modal-btn-cancel" onClick={handleCancelDiagram}>
                                    Cancel
                                </button>
                                <button className="modal-btn-primary" onClick={handleInsertDiagramConfirm}>
                                    Insert Diagram
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default DocumentEditor;
